import React, { useMemo, useState } from "react";
import { useLanguage } from "../../../../contexts";
import { P, motor, useEnergia, useGuardarSeccion } from "../engine";
import { useBorrador } from "../common/useBorrador";
import { Notice, SaveBar, SaveErrors, Section, StatusBadge } from "../common/SectionCommon";
import { GraficoCatalogo } from "../common/Grafico";
import "../common/common.scss";
import "./AutomaticDiagnosis.scss";

const tomar = (inst) => ({ subreq: JSON.parse(JSON.stringify(inst.subreq || {})) });
const CLASE_DICTAMEN = { "badge-good": "Cumple", "badge-warn": "Brecha parcial", "badge-bad": "Brecha mayor" };
const SEV = { high: ["danger", "energy_performance_dq_critical"], med: ["warn", "energy_performance_dq_review"], low: ["info", "energy_performance_dq_info"] };

function FilaRequisito({ f, a }) {
  const st = P.auditStateFor(f, a.cl);
  const visible = P.LBL_VIS[st.label] || st.label;
  return (
    <tr>
      <td className="ep-mono">{a.cl}</td>
      <td>{a.req}<div className="ep-section__note">{st.verif || a.ev}</div></td>
      <td className="ep-section__note">{(st.evid || []).length ? st.evid.map((e, i) => <div key={i}>✓ {e}</div>) : "—"}</td>
      <td><StatusBadge estado={visible} /></td>
      <td className="ep-audit__finding">{st.hallazgo || ""}</td>
    </tr>
  );
}

// Autoevaluación frente a ISO 50001 — 28 requisitos + 121 requisitos de detalle (hojas 08, 15, 26; hoja 01 bloques 6 y 13).
export function AutomaticDiagnosis({ installation, onSaved }) {
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;
  const { archivo, inst, f, error } = useEnergia(installation);
  const { borrador: b, cambiar, dirty, descartar } = useBorrador(inst, tomar);
  const { guardar, saving, errores } = useGuardarSeccion(installation, onSaved, { t });
  const [filtro, setFiltro] = useState("");

  // las respuestas de detalle cambian el estado de los requisitos: se recalcula en vivo
  const vivo = useMemo(() => (archivo && b ? motor.calcularVista({ ...archivo, instalacion: { ...inst, ...b } }) : null), [archivo, inst, b]);
  const fv = (vivo && vivo.f) || f;

  if (!installation) return <p>{t("energy_performance_create_profile_first")}</p>;
  if (!b) return null;
  if (!fv) return <Notice tipo="danger" titulo={t("energy_performance_data_invalid")}>{error}</Notice>;

  const d = P.dictamen(fv);
  const core = P.AUDIT_CHECKLIST.filter((a) => P.CORE_MEDICION_CLS.includes(a.cl));
  const gestion = P.AUDIT_CHECKLIST.filter((a) => !P.CORE_MEDICION_CLS.includes(a.cl));
  const conteo = {};
  P.AUDIT_CHECKLIST.forEach((a) => { const v = P.LBL_VIS[P.auditStateFor(fv, a.cl).label] || "Pendiente"; conteo[v] = (conteo[v] || 0) + 1; });
  const resumen = P.subreqResumen(fv);
  const dq = P.dataQualityChecks(fv);
  const clausulas = [...new Set(P.SUBREQ.map((q) => q.cl))];
  const resp = (id) => (b.subreq[id] || { r: "", ev: "" });
  const setResp = (id, clave, v) => cambiar("subreq", { ...b.subreq, [id]: { ...resp(id), [clave]: v } });

  return (
    <div className="ep-screen ep-audit">
      <h3 className="ep-screen__title">{t("energy_performance_audit_title")}</h3>
      <p className="ep-screen__intro">{t("energy_performance_audit_intro")}</p>

      <Section title={t("energy_performance_measurement_status")}>
        <p><StatusBadge estado={CLASE_DICTAMEN[d.cls] || "Pendiente"}>{d.nivel}</StatusBadge></p>
        <p className="ep-audit__reading">{d.lectura}</p>
      </Section>

      <div className="ep-kpis">
        {P.VIS_ORDEN.map((v) => (
          <div className="ep-kpi" key={v}><div className="ep-kpi__label"><StatusBadge estado={v} /></div><div className="ep-kpi__value">{conteo[v] || 0}</div></div>
        ))}
      </div>

      <div className="ep-charts"><GraficoCatalogo f={fv} id="req_estado" t={t} /><GraficoCatalogo f={fv} id="req_clausula" t={t} /><GraficoCatalogo f={fv} id="phva" t={t} /></div>
      <Section title={t("energy_performance_audit_requirements").replace("{n}", P.AUDIT_CHECKLIST.length)}>
        <div className="ep-table">
          <table>
            <thead><tr><th>{t("energy_performance_clause")}</th><th>{t("energy_performance_requirement")}</th><th>{t("energy_performance_docs_evidence")}</th><th>{t("energy_performance_ecm_status")}</th><th>{t("energy_performance_finding")}</th></tr></thead>
            <tbody>
              <tr className="ep-audit__group ep-audit__group--core"><td colSpan={5}>{t("energy_performance_audit_core")}</td></tr>
              {core.map((a) => <FilaRequisito key={a.cl} f={fv} a={a} />)}
              <tr className="ep-audit__group"><td colSpan={5}>{t("energy_performance_audit_management")}</td></tr>
              {gestion.map((a) => <FilaRequisito key={a.cl} f={fv} a={a} />)}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title={t("energy_performance_subreq_title").replace("{n}", P.SUBREQ.length)} note={t("energy_performance_subreq_note")}>
        <div className="ep-kpis">
          <div className="ep-kpi"><div className="ep-kpi__label">{t("energy_performance_subreq_yes")}</div><div className="ep-kpi__value">{resumen.si}/{resumen.n}</div></div>
          <div className="ep-kpi"><div className="ep-kpi__label">{t("energy_performance_partial")}</div><div className="ep-kpi__value">{resumen.par}</div></div>
          <div className="ep-kpi"><div className="ep-kpi__label">{t("energy_performance_no")}</div><div className="ep-kpi__value">{resumen.no}</div></div>
          <div className="ep-kpi"><div className="ep-kpi__label">{t("energy_performance_subreq_unconfirmed")}</div><div className="ep-kpi__value">{resumen.sin}</div></div>
        </div>
        <label className="ep-section__note" htmlFor="sr-filtro">{t("energy_performance_clause")}: </label>
        <select id="sr-filtro" value={filtro} onChange={(e) => setFiltro(e.target.value)}>
          <option value="">{t("energy_performance_all")}</option>
          {clausulas.map((c) => <option key={c}>{c}</option>)}
        </select>
        <div className="ep-table">
          <table>
            <thead><tr><th>{t("energy_performance_clause")}</th><th>{t("energy_performance_requirement")}</th><th>{t("energy_performance_subreq_met")}</th><th>{t("energy_performance_subreq_evidence")}</th></tr></thead>
            <tbody>
              {P.SUBREQ.filter((q) => !filtro || q.cl === filtro).map((q) => (
                <tr key={q.id}>
                  <td className="ep-mono">{q.cl}</td>
                  <td>{q.t}</td>
                  <td>
                    <select aria-label={`${t("energy_performance_subreq_met")} ${q.id}`} value={resp(q.id).r || ""} onChange={(e) => setResp(q.id, "r", e.target.value)}>
                      {P.SUBREQ_OPC.map((o) => <option key={o} value={o}>{o || t("energy_performance_subreq_unconfirmed")}</option>)}
                    </select>
                  </td>
                  <td><input type="text" aria-label={`${t("energy_performance_subreq_evidence")} ${q.id}`} value={resp(q.id).ev || ""} onChange={(e) => setResp(q.id, "ev", e.target.value)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <SaveErrors errores={errores} t={t} />
        <SaveBar dirty={dirty} invalidos={0} saving={saving} onSave={() => guardar({ subreq: b.subreq })} onDiscard={descartar} t={t} />
      </Section>

      <Section title={t("energy_performance_dq_title")} note={t("energy_performance_dq_note")}>
        {!dq.length && <p className="ep-section__note">{t("energy_performance_dq_none")}</p>}
        {dq.map((x, i) => {
          const [tipo, k] = SEV[x.sev] || SEV.med;
          return <Notice key={i} tipo={tipo} titulo={t(k)}>{x.t}</Notice>;
        })}
      </Section>
    </div>
  );
}
